import {useCallback, useMemo, useReducer} from 'react';
import {useSelector} from 'react-redux';

import BenchlingConfig from 'BenchlingConfig';
import {StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {InventoryStructuredTable} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import getLastSyncedUpstreamValueForColumn from 'notebook/structured-tables/util/getLastSyncedUpstreamValueForColumn';
import {findSchemaFieldByDBId} from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowDataUiData} from 'notebook/tables/constants/RowConstants';
import {InsertableColumnInfo, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {getErrorsAndWarningsFromRow} from 'notebook/tables/errors/CellErrorGetters';
import {PotentialCellErrors, TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell, EvaluatedCellError} from 'notebook/tables/excess/EvaluationConstants';
import * as Util from 'notebook/tables/excess/Util';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import useFunctionIfMounted from 'ui-library/hooks/useFunctionIfMounted';

import useLiveData from '../live-data/useLiveData';
import * as LiveCellValues from '../LiveCellValues';
import {AI_MISMATCH_VALIDATION_RULE_ID, TableValidationCellError} from '../StructuredTableApiConstants';
import {
  CellErrorPosition,
  LiveRowMetadata,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from '../StructuredTableConstants';
import buildRowUiData from '../util/buildRowUiData';
import cellHasOutdatedValue from '../util/cellHasOutdatedValue';
import {InitialCellValue} from '../util/getInitialCellValueFromLiveValue';
import getInitialCellValuesForColumn from '../util/getInitialCellValuesForColumn';
import {getLiveRowMetadataFromRowUpdater} from '../util/getLiveRowMetadataFromRowUpdater';
import {
  applyMergedCellBubbledConditions,
  computeMergedCellBubbledConditions,
} from '../util/mergedCellConditions';

/** Shared so that skipping the unsubmitted pass does not invalidate anything that reads its statuses. */
const EMPTY_CELL_UNSUBMITTED_STATUSES: Array<Array<boolean>> = [] as const;

interface HydratedStructuredTableData {
  table: Table;
  getInitialCellValues: (columnInfo: Partial<InsertableColumnInfo>) => Array<InitialCellValue>;
  // Stale indicators will be embedded directly in `table` as JSX elements. The list of stale cells
  // will also be reported here.
  staleCellIndices: Array<{rowIndex: number; colIndex: number}>;
  liveRowMetadataByRow: Array<LiveRowMetadata>;
  tableStatus?: TableIndicatorStatus;
  errorRows: Array<Array<EvaluatedCellError>>;
}

/**
 * Add any errors that we might have in the `errors` CellErrorPosition array into our table.
 * This function will merge together errors that might already exist in the Table with any
 * new errors found in the CellErrorPosition array as well.
 */
function addErrors(table: Table, errors: Array<CellErrorPosition>): Table {
  return {
    ...table,
    table: table.table.map((row, rIndex) =>
      row.map((cell, cIndex) => {
        const cellErrorPosition = errors.find(
          ({rowIndex, columnIndex, error}) => rowIndex === rIndex && columnIndex === cIndex && error != null
        );
        return {
          ...cell,
          // TODO(BNCH-12094): Allow these errors to stack
          error: cell.error || cellErrorPosition?.error,
        };
      })
    ),
  };
}

export function addValidationErrors(
  table: Table,
  structuredTable: UnknownStructuredTable,
  validationErrors: Array<TableValidationCellError>,
  getMismatchResolutionAction?: (cellId: {rowId: string; columnProvenanceUuid: string}) => () => void
): Table {
  if (!validationErrors) {
    return table;
  }
  const rowIndexByRowId: {[rowId: string]: number} = {};
  structuredTable.rows.forEach((row, index) => {
    rowIndexByRowId[row.rowId] = index;
  });
  const cellIndexByColumnProvenanceUuid: {[columnProvenanceUuid: string]: number} = {};
  structuredTable.columns.forEach((column, index) => {
    cellIndexByColumnProvenanceUuid[column.provenanceUuid] = index;
  });
  const tableWithValidationErrors = {
    ...table,
    table: table.table.map((row) => row.map((cell) => ({...cell}))),
  };
  for (const validationError of validationErrors) {
    const rowIndex = rowIndexByRowId[validationError.rowId];
    const cellIndex = cellIndexByColumnProvenanceUuid[validationError.columnProvenanceUuid];
    if (tableWithValidationErrors.table[rowIndex] && tableWithValidationErrors.table[rowIndex][cellIndex]) {
      const cell = tableWithValidationErrors.table[rowIndex][cellIndex];
      const newErrors = validationError.text.map((error) => {
        if (error.validation_rule_id === AI_MISMATCH_VALIDATION_RULE_ID) {
          const mismatchError = new Util.AgentMismatchError(error.message);
          if (getMismatchResolutionAction) {
            mismatchError.resolutionAction = getMismatchResolutionAction({
              rowId: validationError.rowId,
              columnProvenanceUuid: validationError.columnProvenanceUuid,
            });
            mismatchError.resolutionActionLabel = 'Accept value';
          }
          return mismatchError;
        } else if (error.overridable && error.bypassed) {
          return new Util.BypassedValidationRuleWarning(error.message);
        } else {
          return new Util.ValidationRuleError(error.message, error.overridable);
        }
      });
      cell.error = Util.addError(cell.error, newErrors);
    }
  }
  Util.bubbleMergedCellValidationErrors(tableWithValidationErrors);
  return tableWithValidationErrors;
}

function addUnsubmittedRows(table: Table, rowUnsubmittedStatuses: Array<boolean>): Table {
  return {
    ...table,
    rowData: (table.rowData || []).map((rowDataForRow, i) => ({
      ...rowDataForRow,
      uiData: {
        hasUnsubmittedChanges: rowUnsubmittedStatuses[i],
      },
    })),
  };
}

function addUnsubmittedCells(
  structuredTable: UnknownStructuredTable,
  adapter: UnknownStructuredTableAdapter,
  table: Table,
  cellUnsubmittedStatuses: Array<Array<boolean>>
): Table {
  return {
    ...table,
    table: table.table.map((row, i) => {
      const structuredTableRow = structuredTable.rows[i];
      const {isFinalized, lastSyncedUpstreamValues} = structuredTableRow;
      // Get columns for row to resolve ROW_DELEGATE columns to their delegated column types
      const columns = adapter.getColumnsForRow(structuredTable, structuredTableRow);
      return row.map((cell, j) => {
        if (!isFinalized || !lastSyncedUpstreamValues || !cellUnsubmittedStatuses[i][j]) {
          return cell;
        }
        // Add indicators for cells that have already been submitted and have changes
        const column = columns[j];
        const fieldIntegerId = column.tagSchemaFieldId;
        const schemaField =
          fieldIntegerId != null ? findSchemaFieldByDBId(structuredTable.schemas, fieldIntegerId) : undefined;
        const lastSyncedUpstreamValue = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, {
          ...column,
          schemaFieldApiId: schemaField?.apiId ?? null,
        });
        const lastSyncedUpstreamValueText = lastSyncedUpstreamValue?.value
          ? formatTypedValue(lastSyncedUpstreamValue.value, cell)
          : null;
        return stubbableFunctions.injectUnsubmittedChangesIndicator(cell, lastSyncedUpstreamValueText);
      });
    }),
  };
}

// This adds indicators to the cells, and also returns a list of which cells are stale.
export function addStalenessIndicators(
  table: Table,
  columns: Array<StructuredTableColumn>,
  rowUnsubmittedStatuses: Array<boolean>,
  computedTable: Array<Array<EvaluatedCell>>,
  liveCellValuesForTable: Array<Array<LiveCellValues.LiveCellValue>>,
  lockRowsAfterSubmit: boolean = false,
  // Per-cell last-synced upstream values. When provided for a cell in an unsubmitted row, the
  // staleness check compares the upstream live value against the last-submitted value rather than
  // the user's current edit, enabling both "outdated" and "unsubmitted" indicators simultaneously.
  lastSyncedUpstreamValuesForTable?: Array<Array<TypedValue | null | undefined>>
): {table: Table; staleCellIndices: Array<{rowIndex: number; colIndex: number}>} {
  const staleCellIndices: Array<{rowIndex: number; colIndex: number}> = [];
  const tableWithStaleIndicators = {
    ...table,
    table: liveCellValuesForTable.map((rowLiveCellValues, i) =>
      rowLiveCellValues.map((liveCellValue, j) => {
        // The table "should" always have the correct size, but we've had bugs in the past with empty tables!
        const cell: Cell = table.table[i]?.[j] ?? {text: ''};

        if (
          cellHasOutdatedValue({
            isCellInUnsubmittedRow: rowUnsubmittedStatuses[i],
            structuredTableColumn: columns[j],
            evaluatedValue: computedTable[i]?.[j]?.value,
            liveCellValue,
            tableKind: (table.metadata as StructuredTableToTableMetadata<unknown>)?.kind,
            lockRowsAfterSubmit,
            lastSyncedUpstreamValue: lastSyncedUpstreamValuesForTable?.[i]?.[j],
          })
        ) {
          staleCellIndices.push({rowIndex: i, colIndex: j});
          const liveValueText =
            liveCellValue !== 'NO_VALUE' && liveCellValue != null
              ? formatTypedValue(liveCellValue, cell)
              : null;
          return stubbableFunctions.injectStalenessIndicator(cell, columns[j], liveValueText);
        } else {
          return cell;
        }
      })
    ),
  };
  return {
    table: tableWithStaleIndicators,
    staleCellIndices,
  };
}

interface UseHydratedStructuredTableDataOptions {
  // If your table doesn't have primary samples, set this to false to skip a lot of unnecessary (and
  // potentially crash-inducing) logic.
  hasPrimarySamples?: boolean;

  // Set this to false to prevent the unsubmitted indicator from showing. E.g., inside a creation modal,
  // where "submitted" is not a relevant notion.
  includeUnsubmittedStatus?: boolean;
}

/**
 * This hook prepares some data for use by StructuredTableView (and also StructuredTableFormView).
 * It calculates the `table` (including errors and stale values), and returns a callback to
 * retrieve initial cell values. (Commented below.)
 *
 * It also sets up periodic retrieval of live data.
 */
export default function useHydratedStructuredTableData(params: {
  adapter: UnknownStructuredTableAdapter;
  structuredTable: UnknownStructuredTable;
  computedTable: Array<Array<EvaluatedCell>> | null;
  validationErrors: Array<TableValidationCellError>;
  getMismatchResolutionAction?: (cellId: {rowId: string; columnProvenanceUuid: string}) => () => void;
  inputStoreData?: {
    fileSummariesById?: {[bioentityId: string]: BioentitySummary};
    storableSummariesById?: {[storableId: string]: StorableSummary};
  };
  options?: UseHydratedStructuredTableDataOptions;
}): HydratedStructuredTableData {
  const {adapter, structuredTable, computedTable, validationErrors, getMismatchResolutionAction} = params;
  const {includeUnsubmittedStatus = true, hasPrimarySamples = true} = params.options || {};

  const {primarySamplesByRow, primarySampleLookupInfos} = useMemo(() => {
    let primarySampleLookupInfos: Array<SampleLookupInfo> = [];
    if (hasPrimarySamples) {
      const primarySamplesByRowIds = adapter.getPrimarySamples(structuredTable, computedTable);
      primarySampleLookupInfos = Object.values(primarySamplesByRowIds).flatMap((primarySamples) =>
        primarySamples.flatMap(({sampleLookupInfo}) => sampleLookupInfo ?? [])
      );
      return {
        primarySamplesByRow: structuredTable.rows.map((row) => primarySamplesByRowIds[row.rowId]),
        primarySampleLookupInfos,
      };
    } else {
      return {primarySamplesByRow: new Array(structuredTable.rows.length).fill([]), primarySampleLookupInfos};
    }
  }, [adapter, computedTable, hasPrimarySamples, structuredTable]);

  // As a hack, we can use this `forceUpdate` callback to re-render this component whenever our cache is
  // updated with live data.
  // Currently, we trigger a fetch in useLiveData and asynchronously populate the Apollo cache on completion.
  // However, right after the fetch is triggered, we continue and synchronously read from the cache.
  // This means that if the fetch completes after we read from it, we would be reading stale data. Using
  // `forceUpdate` allows us to trigger a re-render whenever the fetch is complete and synchronously read
  // from the newly updated cache.
  const [forceUpdateState, forceUpdate] = useReducer((x) => x + 1, 0);

  // We should only trigger a re-render once the fetch completes if the component is still mounted.
  const forceUpdateIfMounted = useFunctionIfMounted(forceUpdate);
  // This needs to be memoized as its a dependency for the useEffect in useLiveData.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedForceUpdateIfMounted = useCallback(forceUpdateIfMounted, []) as () => void;

  // During PDF export, live data queries are unnecessary (they only power advisory indicators like
  // dog ears and row strikethrough) but add several seconds per table to the headless render,
  // increasing the likelihood of export timeouts. Gated behind
  // ELN_ENABLE_STRUCTURED_TABLE_LIVE_DATA_SKIP_FOR_EXPORTS; intended to apply unconditionally once validated.
  // `skipLiveData` is a convenience context var that lets us A/B test skipping on specific entries
  // in production (via query param) before flipping the tenant-wide flag.
  const {isExportView, skipLiveData} = EditorPropertiesContext.useContext();
  const skipLiveDataFetch =
    (BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_LIVE_DATA_SKIP_FOR_EXPORTS || Boolean(skipLiveData)) &&
    isExportView;

  // Trigger live-data fetches and re-render on fetch complete.
  useLiveData(
    adapter,
    skipLiveDataFetch ? [] : primarySampleLookupInfos,
    memoizedForceUpdateIfMounted,
    structuredTable
  );

  // Subscribe to Redux changes for resolution results to trigger re-renders when celery tasks complete
  const resolutionResultsByResolutionQuery = useSelector(
    (state: BenchlingState) => state.models.eln.resolutionResultsByResolutionQuery
  );

  const storeData = useMemo(
    () => ({
      ...(params.inputStoreData ?? adapter.getStoreDataForLiveValues(structuredTable)),
      resolutionResultsByResolutionQuery,
    }),
    [adapter, params.inputStoreData, resolutionResultsByResolutionQuery, structuredTable]
  );

  // This callback is used when the user inserts an optional column into a table. It sets the
  // initial values correctly, based on live data, as available
  const getInitialCellValues = useCallback(
    (columnInfo: Partial<InsertableColumnInfo>): Array<InitialCellValue> =>
      getInitialCellValuesForColumn(adapter, structuredTable, columnInfo, computedTable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structuredTable.schemas, primarySamplesByRow, storeData, computedTable]
  );

  // Build the updated table. forceUpdateState will cause all of the memoized factory callbacks to trigger

  // 1. Construct the updated table from the current structured table
  const updatedTableFromStructuredTable = useMemo(() => {
    // Ensures that forceUpdateState is used to force React to run this hook
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    forceUpdateState;
    return adapter.toTable(structuredTable);
  }, [adapter, structuredTable, forceUpdateState]);
  let updatedTable = updatedTableFromStructuredTable;

  updatedTable = useMemo(
    () => addValidationErrors(updatedTable, structuredTable, validationErrors, getMismatchResolutionAction),
    [updatedTable, structuredTable, validationErrors, getMismatchResolutionAction]
  );

  // 2. Add error validation tags to the table
  const {table: updatedTableWithErrors, errorRows: updatedErrorRows} = useMemo(() => {
    // Ensures that forceUpdateState is used to force React to run this hook
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    forceUpdateState;
    return stubbableFunctions.getErrorRows(
      structuredTable,
      updatedTable,
      computedTable,
      adapter,
      includeUnsubmittedStatus
    );
  }, [structuredTable, updatedTable, computedTable, adapter, includeUnsubmittedStatus, forceUpdateState]);
  updatedTable = updatedTableWithErrors;

  // 3. Add unsubmitted changes indicators to the table
  const {
    table: updatedTableWithUnsubmittedIndicators,
    rowUnsubmittedStatuses,
    cellUnsubmittedStatuses,
  } = useMemo(() => {
    // Ensures that forceUpdateState is used to force React to run this hook
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    forceUpdateState;
    return stubbableFunctions.addUnsubmittedChanges(
      structuredTable,
      updatedTable,
      computedTable,
      adapter,
      includeUnsubmittedStatus
    );
  }, [structuredTable, updatedTable, computedTable, adapter, includeUnsubmittedStatus, forceUpdateState]);
  updatedTable = updatedTableWithUnsubmittedIndicators;

  // 4. Add stale error tags to the table using live hydrated data
  const {updatedTableWithStalenessIndicators, updatedStaleCellIndices} = useMemo((): {
    updatedTableWithStalenessIndicators: Table;
    updatedStaleCellIndices: Array<{rowIndex: number; colIndex: number}>;
  } => {
    // Ensures that forceUpdateState is used to force React to run this hook
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    forceUpdateState;
    let updatedStaleCellIndices: Array<{rowIndex: number; colIndex: number}> = [];
    let updatedTableWithStalenessIndicators = updatedTable;
    // Add staleness indicators
    if (hasPrimarySamples) {
      const liveCellValuesForTable: Array<Array<LiveCellValues.LiveCellValue>> =
        LiveCellValues.gatherLiveCellValuesForTable({
          tableSchemas: structuredTable.schemas,
          structuredTableColumns: structuredTable.columns,
          computedTable,
          adapter,
          primarySamplesByRow,
          storeData,
        });
      if (liveCellValuesForTable.length > 0 && computedTable != null) {
        // For inventory tables, unless unlockRowsAfterSubmit is true all rows are fully frozen on submission
        // and thus never considered outdated
        const lockRowsAfterSubmit =
          structuredTable.kind === StructuredTableKind.INVENTORY &&
          !(structuredTable as InventoryStructuredTable)?.metadata?.unlockRowsAfterSubmit;

        // Pre-compute the last-synced value per cell so that addStalenessIndicators can detect
        // upstream changes for unsubmitted rows (where evaluatedValue reflects the user's edit,
        // not the last submission).
        const lastSyncedUpstreamValuesForTable: Array<Array<TypedValue | null | undefined>> =
          structuredTable.rows.map((row) => {
            const {lastSyncedUpstreamValues} = row;
            const rowColumns = adapter.getColumnsForRow(structuredTable, row);
            return rowColumns.map((column) => {
              if (!lastSyncedUpstreamValues) {
                return undefined;
              }
              const fieldIntegerId = column.tagSchemaFieldId;
              const schemaField =
                fieldIntegerId != null
                  ? findSchemaFieldByDBId(structuredTable.schemas, fieldIntegerId)
                  : undefined;
              const lsuv = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, {
                ...column,
                schemaFieldApiId: schemaField?.apiId ?? null,
              });
              return lsuv == null ? undefined : lsuv.value;
            });
          });

        const stalenessInfo = addStalenessIndicators(
          updatedTable,
          structuredTable.columns,
          rowUnsubmittedStatuses,
          computedTable,
          liveCellValuesForTable,
          lockRowsAfterSubmit,
          lastSyncedUpstreamValuesForTable
        );
        updatedTableWithStalenessIndicators = stalenessInfo.table;
        updatedStaleCellIndices = stalenessInfo.staleCellIndices;
      }
    }
    return {updatedTableWithStalenessIndicators, updatedStaleCellIndices};
  }, [
    adapter,
    computedTable,
    hasPrimarySamples,
    primarySamplesByRow,
    rowUnsubmittedStatuses,
    storeData,
    structuredTable,
    updatedTable,
    forceUpdateState,
  ]);
  updatedTable = updatedTableWithStalenessIndicators;

  // 4b. Report conditions belonging to the hidden cells of a merge on the merge's visible cell.
  // Only the top-left cell of a merge renders, so conditions on covered cells — whether the merge is a
  // single input, a manual merge, or a pasted one — are otherwise invisible. Both halves are no-ops for
  // tables without merges, and the second reuses every cell that does not gain a condition, so this is
  // not another whole-table pass.
  const mergedCellBubbledConditions = useMemo(
    () =>
      computeMergedCellBubbledConditions({
        structuredTable,
        cellUnsubmittedStatuses,
        staleCellIndices: updatedStaleCellIndices,
      }),
    [structuredTable, cellUnsubmittedStatuses, updatedStaleCellIndices]
  );
  updatedTable = useMemo(
    () => applyMergedCellBubbledConditions(updatedTable, mergedCellBubbledConditions),
    [updatedTable, mergedCellBubbledConditions]
  );

  // 5. Fetch live table metadata and generate the overall table status
  const {updatedTableWithLiveMetadata, updatedTableStatus, updatedLiveRowMetadataByRow} = useMemo((): {
    updatedTableWithLiveMetadata: Table;
    updatedTableStatus: TableIndicatorStatus;
    updatedLiveRowMetadataByRow: Array<LiveRowMetadata>;
  } => {
    // Ensures that forceUpdateState is used to force React to run this hook
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    forceUpdateState;
    // Add live row-level metadata, e.g. archive status of the row itself
    const updatedLiveRowMetadataByRow = structuredTable.rows.map((row, i) =>
      getLiveRowMetadataFromRowUpdater({
        row,
        primarySamples: primarySamplesByRow[i],
        adapter,
        columns: structuredTable.columns,
      })
    );

    const liveRowUiData = buildRowUiData(structuredTable, updatedLiveRowMetadataByRow);
    const updatedTableWithLiveMetadata = stubbableFunctions.updateTableRowMetadata(
      updatedTable,
      liveRowUiData
    );
    const tableArchivedStatus = adapter.areRequiredSchemasArchived(structuredTable);
    const updatedTableStatus = stubbableFunctions.getTableStatus(
      updatedTableWithLiveMetadata,
      updatedErrorRows,
      rowUnsubmittedStatuses,
      tableArchivedStatus,
      updatedStaleCellIndices.length !== 0
    );
    return {updatedTableWithLiveMetadata, updatedTableStatus, updatedLiveRowMetadataByRow};
  }, [
    adapter,
    updatedErrorRows,
    primarySamplesByRow,
    rowUnsubmittedStatuses,
    updatedStaleCellIndices,
    structuredTable,
    updatedTable,
    forceUpdateState,
  ]);
  updatedTable = updatedTableWithLiveMetadata;

  return {
    getInitialCellValues,
    table: updatedTable,
    errorRows: updatedErrorRows,
    staleCellIndices: updatedStaleCellIndices,
    liveRowMetadataByRow: updatedLiveRowMetadataByRow,
    tableStatus: updatedTableStatus,
  };
}

export const stubbableFunctions = {
  addUnsubmittedChanges(
    structuredTable: UnknownStructuredTable,
    table: Table,
    computedTable: Array<Array<EvaluatedCell>> | null,
    adapter: UnknownStructuredTableAdapter,
    includeUnsubmittedStatus: boolean
  ): {
    table: Table;
    rowUnsubmittedStatuses: Array<boolean>;
    cellUnsubmittedStatuses: Array<Array<boolean>>;
  } {
    let rowUnsubmittedStatuses: Array<boolean>;

    if (!includeUnsubmittedStatus || !computedTable) {
      rowUnsubmittedStatuses = new Array(structuredTable.rows.length).fill(false);
      return {table, rowUnsubmittedStatuses, cellUnsubmittedStatuses: EMPTY_CELL_UNSUBMITTED_STATUSES};
    }

    const cellUnsubmittedStatuses = adapter.getCellUnsubmittedStatuses(structuredTable, computedTable);

    // Apply unsubmitted row status to table
    rowUnsubmittedStatuses = cellUnsubmittedStatuses.map((row) => row.some((cell) => cell));
    const tableWithUnsubmittedRows = addUnsubmittedRows(table, rowUnsubmittedStatuses);

    // Add unsubmitted changes indicators to cells
    const tableWithIndicators = addUnsubmittedCells(
      structuredTable,
      adapter,
      tableWithUnsubmittedRows,
      cellUnsubmittedStatuses
    );
    return {
      table: tableWithIndicators,
      rowUnsubmittedStatuses,
      cellUnsubmittedStatuses,
    };
  },

  injectUnsubmittedChangesIndicator(cell: Cell, lastValue: string | null): Cell {
    let unsubmittedMessage = `This value was updated after the table was last submitted`;
    if (lastValue) {
      unsubmittedMessage += `. Previously submitted value: ${lastValue}`;
    }

    return {
      ...cell,
      error: Util.addError(cell.error, new Util.UnsubmittedChangesError(unsubmittedMessage)) ?? undefined,
    };
  },

  injectStalenessIndicator(cell: Cell, column: StructuredTableColumn, newValue: string | null = null): Cell {
    let staleMessage: string = '';
    if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
      staleMessage = 'Refresh lookup columns for latest value';
    } else {
      staleMessage = 'Re-sync table for latest value';
    }
    if (newValue) {
      staleMessage += `: ${newValue}`;
    }
    return {
      ...cell,
      error: Util.addError(cell.error, new Util.OutdatedError(staleMessage)) ?? undefined,
    };
  },

  /**
   * Given live row metadata, update the rows of a table to reflect it.
   */
  updateTableRowMetadata(table: Table, rowUiDataByRow: Array<RowDataUiData>): Table {
    return {
      ...table,
      rowData: rowUiDataByRow.map((rowUiData, rowIndex) => {
        const existingRowData = table.rowData?.[rowIndex];
        if (existingRowData === undefined) {
          return {uiData: rowUiData};
        } else {
          return {
            ...existingRowData,
            uiData: {
              ...existingRowData.uiData,
              ...rowUiData,
            },
          };
        }
      }),
    };
  },

  // The submission-status subset of this (UNSUBMITTED/SUBMITTED/HAS_UNSUBMITTED_CHANGES) is kept in
  // sync with compute_table_submission_status in
  // benchling/notebook/structured_tables/logic/table_logic.py; keep the ordering consistent.
  getTableStatus(
    table: Table,
    errorRows: Array<Array<EvaluatedCellError>>,
    rowUnsubmittedStatuses: Array<boolean>,
    tableArchivedStatus: boolean,
    outdatedStatus: boolean
  ): TableIndicatorStatus {
    let unsubmittedStatus: boolean = false;
    const resultsAPIStatus: boolean =
      (table.numApiRowsToAdd !== undefined && table.numApiRowsToAdd > 0) ?? false;

    let consolidatedUniqueErrors: PotentialCellErrors = [];
    for (let i = 0; i < errorRows.length; i++) {
      if (table.rowData?.[i].uiData?.archivePurpose == null) {
        if (errorRows[i]) {
          for (const cellError of errorRows[i]) {
            consolidatedUniqueErrors = Util.addError(consolidatedUniqueErrors, cellError);
          }
          if (rowUnsubmittedStatuses[i]) {
            unsubmittedStatus = true;
          }
        }
      }
    }

    const errorStatus: boolean = Util.hasAnyBlockingErrors(consolidatedUniqueErrors);
    const hasOnlyBypassableErrors: boolean = Util.hasOnlyBypassableErrors(consolidatedUniqueErrors);
    const hasOnlyBypassedErrors: boolean = Util.hasOnlyBypassedErrors(consolidatedUniqueErrors);

    const numArchivedRows = table.rowData?.filter((row) => row.uiData?.archivePurpose != null).length ?? 0;
    const hasArchivedRows = numArchivedRows > 0;
    const allRowsArchived = hasArchivedRows && numArchivedRows === table.rowData?.length;
    const hasFinalizedRows = table.rowData?.some((row) => row.isFinalized);

    // Table statuses are mutually exclusively, so they are assigned in order of priority. E.g. A
    // table with unsubmitted changes and an error will only have the ERROR status.
    let tableStatus: TableIndicatorStatus = TableIndicatorStatus.NO_STATUS;
    if (errorStatus) {
      tableStatus = TableIndicatorStatus.ERROR;
    } else if (hasOnlyBypassableErrors) {
      tableStatus = TableIndicatorStatus.HAS_BYPASSABLE_ERRORS;
    } else if (hasOnlyBypassedErrors && !unsubmittedStatus) {
      tableStatus = TableIndicatorStatus.SUBMITTED_BYPASSING_VALIDATION;
    } else if (tableArchivedStatus) {
      tableStatus = TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED;
    } else if (unsubmittedStatus) {
      tableStatus = TableIndicatorStatus.UNSUBMITTED_CHANGES;
    } else if (allRowsArchived && !hasFinalizedRows) {
      tableStatus = TableIndicatorStatus.ALL_ROWS_ARCHIVED_UNSUBMITTED;
    } else if (hasArchivedRows) {
      tableStatus = TableIndicatorStatus.ROWS_ARCHIVED;
    } else if (outdatedStatus || resultsAPIStatus) {
      tableStatus = TableIndicatorStatus.OUT_OF_DATE;
    }
    return tableStatus;
  },

  getErrorRows(
    structuredTable: UnknownStructuredTable,
    table: Table,
    computedTable: Array<Array<EvaluatedCell>> | null,
    adapter: UnknownStructuredTableAdapter,
    includeUnsubmittedStatus: boolean
  ): {table: Table; errorRows: Array<Array<EvaluatedCellError>>} {
    let errorRows: Array<Array<EvaluatedCellError>> = [];
    if (includeUnsubmittedStatus && computedTable) {
      errorRows = structuredTable.rows.map((structuredTableRow, i) =>
        getErrorsAndWarningsFromRow(table.table[i], computedTable[i])
      );
    }

    // Add errors
    const errors: Array<CellErrorPosition> = [];
    if (computedTable) {
      for (const tableValidationFn of adapter.getTableValidationFns()) {
        for (const error of tableValidationFn(structuredTable, computedTable)) {
          if (error != null) {
            errors.push(error);
          }
        }
      }
      table = addErrors(table, errors);
      errors.forEach((error) => {
        if (error.error != null) {
          errorRows[error.rowIndex].push(error.error);
        }
      });
    }
    return {table, errorRows};
  },
};
